import { createSelector } from 'reselect';
import { selectAccounts, selectCurrentAccountId } from './accounts';

const selectUserId = (s) => s.app.user.id;
const selectAllClientDetails = (s) => s.clients.clientDetails;
export const selectCurrentClientId = (state) => state.clients.clientId;

export const selectClientDetails = createSelector(
  (s, id) => id ?? selectUserId(s),
  selectAllClientDetails,
  (id, details) => details[id]
);

const selectAccountOwners = createSelector(
  (s) => s.accounts.owners,
  selectCurrentAccountId,
  (owners, id) => owners[id]
);

export const selectAccountOwner = createSelector(selectAccountOwners, (owners) => owners?.[0]);

export const selectClientDetailsFromAccount = createSelector(
  selectAccountOwner,
  selectAllClientDetails,
  (owner, details) => (owner ? details[owner.userId] : undefined)
);

const selectAllAddresses = (s) => s.addresses.items;

export const selectClientAddresses = createSelector(
  (_, id) => id,
  selectAllAddresses,
  (id, addresses) => addresses[id]
);

const selectAllClientAccounts = (s) => s.clients.accounts;
const selectAllAccountsAums = (state) => state.accounts.aums;

export const selectClientAccounts = createSelector(
  (s, id) => id ?? selectUserId(s),
  selectAllClientAccounts,
  selectAllAccountsAums,
  (id, clientAccounts, aums) => {
    if (!(id in clientAccounts)) return undefined;
    const accounts = [];
    clientAccounts[id].forEach((a) => {
      accounts.push(buildAccount({ account: a, aums }));
    });

    return accounts.length > 0 ? accounts : undefined;
  }
);

const selectCountries = (state) => state.statics.countries;
const selectDocumentTypes = (state) => state.statics.documentTypes;

export const selectAccountOwnerAddress = createSelector(
  selectAccountOwner,
  selectAllAddresses,
  selectCountries,
  (owner, addresses, countries) =>
    owner && addresses[owner.userId]
      ? addresses[owner.userId]
          .filter((a) => !a.isDeleted)
          .map((aa) => ({
            ...aa,
            country: countries.find((c) => c.id === aa.countryId) || {},
          }))
      : []
);

const selectAllDocs = (s) => s.documents.items;

export const selectAccountOwnerDocuments = createSelector(
  selectAccountOwner,
  selectAllDocs,
  selectCountries,
  selectDocumentTypes,
  (owner, documents, countries, documentTypes) =>
    owner && documents[owner.userId]
      ? documents[owner.userId]
          .filter((a) => !a.isDeleted)
          .map((aa) => ({
            ...aa,
            country: countries.find((c) => c.id === aa.countryId) || {},
            documentType: documentTypes.find((c) => c.id === aa.documentTypeId) || {},
          }))
      : []
);

const buildAccount = (accountsData) => {
  const { account, aums } = accountsData;
  if (!account) return null;

  return {
    ...account,
    aums: aums ? aums[account.id] : undefined,
  };
};

const selectAllAssetBrakdowns = (state) => state.clients.assetsBreakdown;

export const selectClientAssetsBreakdown = createSelector(
  (s, id) => id,
  selectAllAssetBrakdowns,
  (id, breakdowns) => breakdowns[id]
);
